import { useState, useRef, useCallback, useEffect } from "react";
import {
  BrowserMultiFormatReader,
  DecodeHintType,
  BarcodeFormat,
} from "@zxing/library";
import { message } from "antd";
import {
  playBeepSound,
  triggerVibration,
  sanitizeScannedBarcode,
  getCameraStream,
} from "../Utils";

import { LastScannedItem } from "../components/BarcodeScanner/ScannedItemBanner";

interface UseBarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScanSuccess: (scannedCode: string) => Promise<boolean | void> | boolean | void;
  autoCloseOnSuccess?: boolean;
}

export const useBarcodeScanner = ({
  open,
  onClose,
  onScanSuccess,
  autoCloseOnSuccess = false,
}: UseBarcodeScannerProps) => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [lastScanned, setLastScanned] = useState<LastScannedItem | null>(null);
  const [hasTorch, setHasTorch] = useState<boolean>(false);
  const [torchOn, setTorchOn] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerRef = useRef<BrowserMultiFormatReader | null>(null);
  const isProcessingScanRef = useRef<boolean>(false);
  const lastScannedCodeRef = useRef<{ code: string; time: number }>({
    code: "",
    time: 0,
  });

  const stopScanner = useCallback(() => {
    if (scannerRef.current) {
      try {
        scannerRef.current.reset();
      } catch (err) {
        console.warn("Error resetting ZXing:", err);
      }
      scannerRef.current = null;
    }

    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("Error stopping stream tracks:", err);
      }
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);
    setHasTorch(false);
    setTorchOn(false);
  }, []);

  const handleScan = useCallback(
    async (decodedText: string) => {
      const now = Date.now();
      if (
        lastScannedCodeRef.current.code === decodedText &&
        now - lastScannedCodeRef.current.time < 1800
      ) {
        return;
      }

      if (isProcessingScanRef.current) return;
      isProcessingScanRef.current = true;
      lastScannedCodeRef.current = { code: decodedText, time: now };

      playBeepSound();
      triggerVibration();

      setLastScanned({ code: decodedText, timestamp: now, message: "Processing...", quantity: 1 });

      try {
        const result = await onScanSuccess(decodedText);
        const isSuccessful = result !== false;

        setLastScanned({
          code: decodedText,
          timestamp: now,
          success: isSuccessful,
          message: isSuccessful ? "Item added to bill!" : "Product not found",
          quantity: 1,
        });

        if (autoCloseOnSuccess) {
          stopScanner();
          onClose();
        }
      } catch (err) {
        setLastScanned({
          code: decodedText,
          timestamp: now,
          success: false,
          message: "Error processing item scan",
          quantity: 1,
        });
      } finally {
        setTimeout(() => {
          isProcessingScanRef.current = false;
        }, 1200);
      }
    },
    [onScanSuccess, autoCloseOnSuccess, stopScanner, onClose]
  );

  const toggleTorch = useCallback(async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (track && track.getCapabilities) {
      const caps = track.getCapabilities() as any;
      if (caps.torch) {
        const nextState = !torchOn;
        try {
          await track.applyConstraints({
            // @ts-ignore
            advanced: [{ torch: nextState }],
          });
          setTorchOn(nextState);
        } catch (err) {
          console.warn("Torch toggle failed", err);
        }
      }
    }
  }, [torchOn]);

  const startScanner = useCallback(async () => {
    stopScanner();

    try {
      const stream = await getCameraStream();

      try {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && videoTrack.getCapabilities) {
          const caps = videoTrack.getCapabilities() as any;
          const advancedConstraints: any = {};
          if (caps.focusMode && caps.focusMode.includes("continuous")) {
            advancedConstraints.focusMode = "continuous";
          }
          if (caps.exposureMode && caps.exposureMode.includes("continuous")) {
            advancedConstraints.exposureMode = "continuous";
          }
          if (caps.whiteBalanceMode && caps.whiteBalanceMode.includes("continuous")) {
            advancedConstraints.whiteBalanceMode = "continuous";
          }
          if (Object.keys(advancedConstraints).length > 0) {
            await videoTrack.applyConstraints({
              // @ts-ignore
              advanced: [advancedConstraints],
            });
          }
          if (caps.torch) {
            setHasTorch(true);
          }
        }
      } catch {
        // Focus/Torch constraint unsupported
      }

      streamRef.current = stream;

      if (!videoRef.current) {
        throw new Error("Video element not ready.");
      }

      const video = videoRef.current;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.muted = true;
      video.autoplay = true;
      video.srcObject = stream;

      try {
        await video.play();
      } catch (playErr) {
        console.warn("Video play exception:", playErr);
      }

      setIsScanning(true);

      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.ENABLE_CODE_39_EXTENDED_MODE, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.CODABAR,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.ITF,
        BarcodeFormat.PDF_417,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
        BarcodeFormat.AZTEC,
      ]);

      const codeReader = new BrowserMultiFormatReader(hints);
      scannerRef.current = codeReader;

      let nativeDetector: any = null;
      if (typeof window !== "undefined" && "BarcodeDetector" in window) {
        try {
          // @ts-ignore
          const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
          if (supportedFormats && supportedFormats.length > 0) {
            // @ts-ignore
            nativeDetector = new window.BarcodeDetector({ formats: supportedFormats });
          }
        } catch (e) {
          console.warn("Native BarcodeDetector init fallback:", e);
        }
      }

      if (nativeDetector) {
        let isDetecting = false;
        const nativeLoop = async () => {
          if (!streamRef.current || !videoRef.current) return;
          const v = videoRef.current;
          if (v.readyState >= 2 && v.videoWidth > 0 && !isDetecting) {
            isDetecting = true;
            try {
              const barcodes = await nativeDetector.detect(v);
              if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                const clean = sanitizeScannedBarcode(barcodes[0].rawValue);
                if (clean) {
                  await handleScan(clean);
                }
              }
            } catch {
              // Ignore frame detection error
            } finally {
              isDetecting = false;
            }
          }
          if (streamRef.current) {
            requestAnimationFrame(nativeLoop);
          }
        };
        requestAnimationFrame(nativeLoop);
      }

      codeReader.decodeFromStream(stream, video, (result) => {
        if (result) {
          const rawText = result.getText();
          const cleanText = sanitizeScannedBarcode(rawText);
          if (cleanText) {
            handleScan(cleanText);
          }
        }
      });
    } catch (err: any) {
      console.error("Camera startup failed:", err);
      message.error(err?.message || "Could not access camera. Please allow camera permissions.");
      setIsScanning(false);
    }
  }, [stopScanner, handleScan]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open) {
      timer = setTimeout(() => {
        startScanner();
      }, 250);
    } else {
      stopScanner();
      setLastScanned(null);
    }

    return () => {
      clearTimeout(timer);
      stopScanner();
    };
  }, [open, startScanner, stopScanner]);

  return {
    videoRef,
    isScanning,
    lastScanned,
    hasTorch,
    torchOn,
    toggleTorch,
    stopScanner,
  };
};
